import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PenTool, IndianRupee, Send, Check, X, MessageSquare, Loader2, ArrowLeftRight,
} from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { EmptyState } from "@/components/EmptyState";
import { format } from "date-fns";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-[#F1EDDF] text-[#4A5A50]",
    designer_offered: "bg-[#F6EED8] text-[#6E5416]",
    counter_from_order_team: "bg-blue-50 text-blue-700",
    accepted: "bg-[#E1F0E6] text-[#0F5E39]",
    rejected: "bg-red-50 text-red-700",
  };
  const label = (status || "pending").replaceAll("_", " ");
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${map[status] || map.pending}`}>{label}</span>;
}

function OfferDialog({ open, onOpenChange, quote, onDone, isDesigner }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [action, setAction] = useState("submit");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!quote) return;
    // Prefill sensible default: last counterparty offer amount, else current cad_cost.
    const lastOffer = quote.offers?.[quote.offers.length - 1];
    setAmount(lastOffer?.amount || quote.cad_cost || "");
    setNote("");
    // designer's action defaults: submit if no offers, counter/accept if there was a counter.
    if (!quote.offers || quote.offers.length === 0) setAction("submit");
    else setAction(isDesigner ? "counter" : "counter");
  }, [quote, isDesigner]);

  if (!quote) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { amount: Number(amount) || 0, note: note || null, action };
      await api.post(`/cad/quotes/${quote.id}/offer`, body);
      toast.success(`${action} recorded`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="offer-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">CAD charge negotiation</DialogTitle>
          <DialogDescription>Order {quote.order_reference || quote.order_id?.slice(0, 8)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-xl border border-[#E4DFCF] p-3 max-h-56 overflow-y-auto">
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2">History</div>
            {(quote.offers || []).length === 0 && <div className="text-sm text-[#4A5A50]">No offers yet.</div>}
            {(quote.offers || []).map((o) => (
              <div key={o.id} className="flex items-start gap-2 py-1.5 border-b border-[#E4DFCF] last:border-0">
                <div className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${o.by_role === "cad_designer" ? "bg-[#F6EED8] text-[#6E5416]" : "bg-blue-50 text-blue-700"}`}>{o.by_role.replace("_", " ")}</div>
                <div className="flex-1">
                  <div className="font-mono text-sm">₹{Number(o.amount).toLocaleString("en-IN")} · <span className="text-[#4A5A50]">{o.action}</span></div>
                  {o.note && <div className="text-xs text-[#4A5A50]">"{o.note}"</div>}
                  <div className="text-[10px] text-[#4A5A50]">{o.by_user_name} · {format(new Date(o.created_at), "d MMM HH:mm")}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount ₹</Label>
              <div className="relative mt-1">
                <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5A50]"/>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-9 h-11" data-testid="offer-amount"/>
              </div>
            </div>
            <div>
              <Label>Action</Label>
              <select value={action} onChange={(e) => setAction(e.target.value)} className="mt-1 h-11 w-full rounded-md border border-[#E4DFCF] bg-white px-3 text-sm" data-testid="offer-action">
                <option value="submit">Submit</option>
                <option value="counter">Counter</option>
                <option value="accept">Accept</option>
                <option value="reject">Reject</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this amount?" className="mt-1"/>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={submit} disabled={busy} data-testid="offer-submit-btn">
            {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Send size={14} className="mr-1"/>}
            {action[0].toUpperCase() + action.slice(1)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CADDesignerPage() {
  const { user } = useAuth();
  const isDesigner = user?.role === "cad_designer";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cad/designer/queue");
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openQuick = (q) => setActive(q);

  return (
    <div className="space-y-6" data-testid="cad-designer-page">
      <PageHead
        eyebrow={isDesigner ? "CAD Designer" : "CAD · Coordination"}
        title={isDesigner ? "My CAD Queue" : "CAD Designer Queue"}
        subtitle={isDesigner
          ? "Your assigned quotes + unassigned jobs. Propose a charge or accept a counter."
          : "All CAD quotes in the negotiation loop across designers."}
      />

      {rows.length === 0 && !loading ? (
        <EmptyState
          icon={PenTool}
          eyebrow="No CAD work yet"
          title="Nothing waiting on you"
          description={isDesigner
            ? "When an Order Team member routes a custom design to CAD, it will appear here."
            : "Route a customer-external item to CAD from the Order detail page and it will show up here."}
        />
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
                  <th className="p-3 text-left">Order</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Negotiation</th>
                  <th className="p-3 text-left">My charge ₹</th>
                  <th className="p-3 text-left">Offers</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((q) => {
                  const last = q.offers?.[q.offers.length - 1];
                  return (
                    <tr key={q.id} className="border-t border-[#E4DFCF] hover:bg-[#F6F1E1]/40" data-testid={`quote-row-${q.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{q.order_reference || q.order_id?.slice(0, 8)}</div>
                        {q.remarks && <div className="text-xs text-[#4A5A50] max-w-md truncate">{q.remarks}</div>}
                      </td>
                      <td className="p-3"><StatusBadge status={q.status}/></td>
                      <td className="p-3"><StatusBadge status={q.negotiation_status || "pending"}/></td>
                      <td className="p-3 font-mono">₹{Number(q.cad_cost || 0).toLocaleString("en-IN")}</td>
                      <td className="p-3">
                        <div className="text-xs">
                          {q.offers?.length || 0} exchange{(q.offers?.length || 0) === 1 ? "" : "s"}
                          {last && <div className="text-[#4A5A50]">last: {last.by_role.replace("_", " ")} → ₹{Number(last.amount).toLocaleString("en-IN")}</div>}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-9"
                          onClick={() => openQuick(q)}
                          disabled={q.negotiation_status === "accepted" || q.negotiation_status === "rejected"}
                          data-testid={`offer-open-${q.id}`}
                        >
                          <ArrowLeftRight size={14} className="mr-1"/> Negotiate
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <OfferDialog
        open={!!active}
        onOpenChange={(o) => !o && setActive(null)}
        quote={active}
        onDone={load}
        isDesigner={isDesigner}
      />
    </div>
  );
}
